import React, {useMemo, useRef, useState} from 'react';
import {Autocomplete, Box, Chip, InputBase} from '@mui/material';
import {NAVY, TEXTE, TEXTE_SECONDAIRE} from '../tokens.js';
import {filtrerCircuits, listeCircuits} from '../selectors/circuits.js';

// Lot 7 C2 : sélecteur de circuits (= patrimoines), partagé par la carte et le dashboard.
// `valeur` = ids de patrimoines ; `onChange(ids)` reçoit la nouvelle liste triée.

const GROUPES = {intra: 'Intramuros', extra: 'Extramuros'};

const Pastille = ({couleur, taille = 10}) => (
    <Box component="span" aria-hidden="true" sx={{flex: '0 0 auto', width: taille, height: taille, borderRadius: '50%', backgroundColor: couleur}}/>
);

/** En-tête de la liste déroulante : Tout / Aucun. Les clics ne retirent pas le focus du champ. */
function PapierCircuits({onTout, onAucun}) {
    return function Papier({children, ...props}) {
        const bouton = {border: 0, background: 'none', font: 'inherit', fontSize: 12, fontWeight: 700, color: NAVY, cursor: 'pointer', p: '2px 6px'};
        return (
            <Box {...props} sx={{borderRadius: '16px', border: '1px solid #D5DAE1', boxShadow: '0 8px 24px rgba(16,46,105,.16)', overflow: 'hidden', backgroundColor: '#FFFFFF'}}>
                <Box onMouseDown={(e) => e.preventDefault()} sx={{display: 'flex', gap: '8px', p: '8px 14px', borderBottom: '1px solid #EEF1F4'}}>
                    <Box component="button" type="button" onClick={onTout} sx={bouton}>Tout</Box>
                    <Box component="button" type="button" onClick={onAucun} sx={bouton}>Aucun</Box>
                </Box>
                {children}
            </Box>
        );
    };
}

export default function CircuitsSelect({index, valeur, onChange, largeur = 300, inerte = false}) {
    const options = useMemo(() => (index ? listeCircuits(index) : []), [index]);
    const choisis = useMemo(() => new Set(valeur), [valeur]);
    const selection = useMemo(() => options.filter((o) => choisis.has(o.id)), [options, choisis]);
    // La saisie est conservée quand on coche une option (recherche « herblain » puis plusieurs circuits à la suite).
    const [saisie, setSaisie] = useState('');
    const emettre = (ids) => onChange([...ids].sort((a, b) => a - b));

    // Composant stable (sinon la liste est remontée à chaque rendu) : les actions passent par une ref.
    const actions = useRef(null);
    actions.current = {tout: () => emettre(options.map((o) => o.id)), aucun: () => emettre([])};
    const Papier = useMemo(() => PapierCircuits({onTout: () => actions.current.tout(), onAucun: () => actions.current.aucun()}), []);

    const resume = selection.length === options.length && options.length > 0
        ? `Tous les circuits (${options.length})`
        : selection.length === 0 ? 'Aucun circuit' : null;

    return (
        <Autocomplete
            multiple
            disableCloseOnSelect
            inputValue={saisie}
            onClose={() => setSaisie('')}
            onInputChange={(e, texte, raison) => { if (raison !== 'reset') setSaisie(texte); }}
            disabled={inerte}
            size="small"
            options={options}
            value={selection}
            onChange={(e, suite) => emettre(suite.map((o) => o.id))}
            groupBy={(o) => GROUPES[o.groupe] || 'Autres'}
            getOptionLabel={(o) => o.libelle || ''}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            filterOptions={(opts, {inputValue}) => filtrerCircuits(opts, inputValue)}
            noOptionsText="Aucun circuit"
            slots={{paper: Papier}}
            slotProps={{popper: {placement: 'bottom-start', style: {width: 400}}}}
            sx={{width: largeur, flex: '0 0 auto'}}
            renderTags={(tags, getTagProps) => (resume ? null : tags.slice(0, 2).map((o, i) => {
                const {key, ...props} = getTagProps({index: i});
                return (
                    <Chip
                        key={key}
                        {...props}
                        size="small"
                        label={o.libelle}
                        sx={{maxWidth: 110, height: 24, fontSize: 12, fontWeight: 600, color: '#FFFFFF', backgroundColor: o.couleur, '& .MuiChip-deleteIcon': {color: 'rgba(255,255,255,.75)'}}}
                    />
                );
            }).concat(tags.length > 2 ? [<Box key="plus" component="span" sx={{fontSize: 12, fontWeight: 700, color: TEXTE_SECONDAIRE, whiteSpace: 'nowrap'}}>+{tags.length - 2}</Box>] : []))}
            renderOption={(props, o) => {
                const {key, ...reste} = props;
                return (
                    <Box component="li" key={key} {...reste} sx={{display: 'flex', alignItems: 'center', gap: '8px', fontSize: 13, color: TEXTE}}>
                        <Pastille couleur={o.couleur}/>
                        <Box component="span" sx={{flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                            {o.libelle}{o.retire ? ' (retirés)' : ''}
                        </Box>
                        <Box component="span" sx={{fontWeight: 700, color: NAVY}}>{o.nbPav}</Box>
                    </Box>
                );
            }}
            renderInput={(params) => {
                const {InputProps, inputProps, id} = params;
                return (
                    <Box ref={InputProps.ref} id={id} sx={{display: 'flex', alignItems: 'center', gap: '6px', height: 36, px: '12px', border: '1px solid #D5DAE1', borderRadius: '999px', backgroundColor: '#FFFFFF', overflow: 'hidden', '&:focus-within': {borderColor: NAVY}}}>
                        <Box component="span" sx={{fontSize: 12, color: TEXTE_SECONDAIRE, flex: '0 0 auto'}}>Circuits</Box>
                        {InputProps.startAdornment}
                        {resume && <Box component="span" sx={{fontSize: 13, fontWeight: 600, color: TEXTE, whiteSpace: 'nowrap'}}>{resume}</Box>}
                        <InputBase
                            inputProps={{...inputProps, 'aria-label': 'Rechercher un circuit'}}
                            sx={{flex: '1 1 40px', minWidth: 40, fontSize: 13, '& input': {p: 0}}}
                        />
                        {InputProps.endAdornment}
                    </Box>
                );
            }}
        />
    );
}
